import React, { useState, Fragment } from "react";
import {
  Heading,
  Button,
  Content,
  Box,
  Columns,
  Hero
} from "react-bulma-components";
import {
  Field as BulmaField,
  Help,
  Textarea
} from "react-bulma-components/lib/components/form";
import Link from "next/link";
import { displayCurrency } from "../../utils";
import { colors, COLORS, FRONTEND, FUNCTION, UNDEFINED } from "../../constants";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import Input from "../inputs/SimpleInputField";
import RecaptchaInput from "../inputs/RecaptchaInput";
import CheckboxInput from "../inputs/CheckboxInputField";
import Select from "../inputs/Select";
import AnonymousApplication from "./AnonymousApplication";
import { useAuth } from "../../lib/use-auth";
import { Control } from "react-bulma-components/src/components/form";
import { firebase, db, fieldValue } from "../../lib/firebase";
import SignIn from "./../auth/SignIn";
import { useRouter } from "next/router";
import { withAuthModal } from "../auth";

const shareLinkFormSchema = Yup.object().shape({
  name: Yup.string()
    .required("Required")
    .default(""),
  candidateName: Yup.string().default(""),
  howLong: Yup.number()
    .positive("Must be positive")
    .required("Required")
    .default(0),
  howLongUnit: Yup.string()
    .required("Required")
    .default("YEARS"),
  remarks: Yup.string().max(1000, "At most 1000 characters accepted").required("Required"),
  relationType: Yup.string()
    .required("Required")
    .max(1000, "At most 1000 characters accepted"),
  anonymous: Yup.boolean().default(false),
  terms: Yup.boolean()
    .oneOf([true], "Required")
    .default(false)
});

const CHOICES = {
  REFER: "REFER",
  APPLY: "APPLY"
};

const termsLabel = (
  <Fragment>
    I agree to the{" "}
    <Link href="/policies/terms-of-service" passHref>
      <a>terms and conditions</a>
    </Link>{" "}
    and{" "}
    <Link href="/policies/terms-of-service" passHref>
      <a>privacy policy</a>
    </Link>
    .
  </Fragment>
);

const AnonymousView = ({ openAuthModal, job, relay }) => {
  const { user } = useAuth();
  const router = useRouter();
  // const referralLink = user ? `${FRONTEND}/refer/${job.id}/${user.uid}` : null;
  const [referralLink, setReferralLink] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [choice, setChoice] = useState(null);
  const recaptchaRef = React.createRef();
  const DURATION_UNITS = [
    {
      value: "HOURS",
      label: "Hours"
    },
    {
      value: "DAYS",
      label: "Days"
    },
    {
      value: "WEEKS",
      label: "Weeks"
    },
    {
      value: "MONTHS",
      label: "Months"
    },
    {
      value: "YEARS",
      label: "Years"
    }
  ];
  const anonymousLabel = "Make this an anonymous referral";

  const referralForm = !referralLink && (
    <Formik
      validationSchema={shareLinkFormSchema}
      initialValues={{
        ...shareLinkFormSchema.default(),
        name: user?.displayName
      }}
      onSubmit={async (values, { setSubmitting }) => {
        const valuesCopy = {
          ...values
        };
        delete valuesCopy.terms;
        setSubmitting(true);
        const ref = await db.collection(`jobs/${job.id}/referrals`).add({
          ...valuesCopy,
          referrer: {
            uid: user.uid
          },
          job: {
            id: job.id,
            headline: job.headline,
            company: job.company
          },
          created: fieldValue.serverTimestamp(),
          updated: fieldValue.serverTimestamp()
        });
        const jobRef = db.doc(`jobs/${job.id}`);
        await jobRef.update({ numReferrals: fieldValue.increment(1) });
        setSubmitting(false);
        setReferralLink(`${FRONTEND}/jobs/${job.id}/referrals/${ref.id}`);
        if (typeof(window.fbq) === FUNCTION) {
          window.fbq("track", "CompleteRegistration", {
            content_name: "Referral",
            currency: "EUR",
            status: true,
            value: 0
          });
        }
      }}
    >
      {({
        errors,
        values,
        touched,
        isValid,
        isSubmitting,
        submitForm,
        setFieldTouched,
        setFieldValue
      }) => (
        <Form>
          <Content>
            Generate a referral link by filling in some details.
          </Content>
          <Columns>
            <Columns.Column>
              <Input name="name" label="Your name" />
              <Help>
                Revealed only to the candidate incase of anonymous referrals.
              </Help>
            </Columns.Column>
            <Columns.Column>
              <Input name="candidateName" label="Candiate name" />
              <Help>Optional</Help>
            </Columns.Column>
          </Columns>
          <BulmaField>
            <CheckboxInput name="anonymous" label={anonymousLabel} />
            <Help>
              By checking this you'll stay anonymous from {job.company.name}.
              You'll still get the Bounty if the candidate gets hired.
            </Help>
          </BulmaField>
          <BulmaField>
            <label>
              How long do you know {values.candidateName || "the candidate"}?
            </label>
            <Columns>
              <Columns.Column size={3}>
                <Control>
                  <Field as={Input} name="howLong" type="number" />
                </Control>
              </Columns.Column>
              <Columns.Column size={3}>
                <Field
                  name="howLongUnit"
                  as={Select}
                  isMulti={false}
                  isCreatable={false}
                  options={DURATION_UNITS}
                  isClearable={false}
                  setFieldValue={setFieldValue}
                  setFieldTouched={setFieldTouched}
                />
              </Columns.Column>
            </Columns>
          </BulmaField>
          <BulmaField>
            <label>What's your relationship?</label>
            <Field
              name="relationType"
              as={Input}
              placeholder="We were colleagues..."
            />
          </BulmaField>
          <BulmaField>
            <label>
              Any further remarks about{" "}
              {values.candidateName || "the candidate"}?
            </label>
            <Field
              name="remarks"
              as={Textarea}
              placeholder={`${values.candidateName ||
                "The candidate"} fits this role because...`}
            />
            {errors.remarks && touched.remarks ? (
              <Help color={colors.danger}>{errors.remarks}</Help>
            ) : null}
            <Help>
              Optional. Help the team at {job.company.name} make an informed
              decision.
            </Help>
          </BulmaField>
          <CheckboxInput name="terms" label={termsLabel} />
          {errors.server && touched.server ? (
            <Help color={colors.danger}>{errors.server}</Help>
          ) : null}
          <BulmaField>
            <Button
              disabled={isSubmitting}
              loading={isSubmitting}
              type="submit"
              color={COLORS.DARK}
            >
              Generate
            </Button>
          </BulmaField>
        </Form>
      )}
    </Formik>
  );
  const referringInfo = (
    <Box>
      {referralLink ? (
        <Content>
          Share this link to refer your friend.
          <br />
          <br /> Click to copy to your clipboard.{" "}
          {linkCopied && "Copied to your clipboard!"}
          <pre
            onClick={() => {
              navigator.clipboard.writeText(referralLink).then(() => {
                setLinkCopied(true);
              });
            }}
          >
            {referralLink}
          </pre>
        </Content>
      ) : (
        referralForm
      )}
    </Box>
  );

  const applicationInfo = (
    <Box>
      <AnonymousApplication job={job} recaptchaRef={recaptchaRef} />
    </Box>
  );

  const loggedInActions = (
    <Fragment>
      {choice && choice === CHOICES.APPLY && applicationInfo}
      {choice && choice === CHOICES.REFER && referringInfo}
    </Fragment>
  );
  return (
    <Fragment>
      <Hero color={COLORS.PRIMARY} gradient>
        <Hero.Body>
          Apply yourself or refer someone.
          <Content>
            <ul>
              <li>
                Earn <b>{displayCurrency(job.bounty, job.bountyCurrency)}</b>{" "}
                for a <u>successful</u> application/referral.
              </li>
              <li>
                This bounty is paid {job.payoutDelay} days after the candidate
                joins the company.{" "}
                <u>Please provide your best contact details.</u>
              </li>
              {/* <li>
            <u>One person can refer one candidate</u> to this job. We'll
            verify identity at the time of payout and circumventing this
            rule will disqualify you from earning bounty.
          </li> */}
            </ul>
            Please further note:
            <ul>
              <li>
                <u>In case of a direct application</u>, bounty is paid to the
                candidate.
              </li>
              <li>
                <u>In case of a referral</u>, bounty is paid <b>only</b> to the
                referrer.
              </li>
            </ul>
            Questions? We have an{" "}
            <a
              href="https://blog.bountyhiring.com/frequently-asked-questions/"
              target="_blank"
            >
              FAQ
            </a>
          </Content>
        </Hero.Body>
      </Hero>
      <Content></Content>
      <Button.Group position="centered" size={"medium"}>
        <Button
          onClick={() => {
            if (!user) {
              openAuthModal(true);
            }
            setChoice(CHOICES.APPLY);
          }}
          color={COLORS.PRIMARY}
        >
          Apply
        </Button>
        <Button
          onClick={() => {
            if (!user) {
              openAuthModal(true);
            }
            setChoice(CHOICES.REFER);
          }}
          color={COLORS.INFO}
        >
          Refer someone
        </Button>
      </Button.Group>
      {/* Show forms when user has interacted */}
      {user && choice && loggedInActions}
    </Fragment>
  );
};

export default withAuthModal(AnonymousView, true);
